import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useParams } from 'react-router';
import type { RuleTemplate, TemplateOwnerType } from '../../../types/frontend';
import { useEventManagementService } from '../../event-management/services/eventManagementService';
import { templateNotificationsToTargets } from '../../event-management/utils/ruleFormUtils';

// ── event_pattern summariser ──────────────────────────────────────────────────

function toArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'string') return [v];
  return [];
}

function formatSource(sources: string[]): string {
  return sources
    .map((s) => s.replace(/^aws\./, 'AWS ').replace(/\./g, ' / '))
    .join(', ');
}

function summarizeEventPattern(pattern: Record<string, unknown>): string {
  const parts: string[] = [];

  const sources = toArray(pattern['source']);
  const detailTypes = toArray(pattern['detail-type']);

  if (detailTypes.length > 0) {
    parts.push(detailTypes.join(' or '));
  }

  if (sources.length > 0) {
    parts.push(`from ${formatSource(sources)}`);
  }

  const detail = pattern['detail'];
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailKeys = Object.keys(detail as Record<string, unknown>);
    if (detailKeys.length > 0) {
      const fieldSummaries = detailKeys.slice(0, 3).map((k) => {
        const values = toArray((detail as Record<string, unknown>)[k]);
        if (values.length === 0) return k;
        if (values.length === 1) return `${k} = ${values[0]}`;
        return `${k} in [${values.join(', ')}]`;
      });
      const suffix = detailKeys.length > 3 ? ` (+${detailKeys.length - 3} more)` : '';
      parts.push(`where ${fieldSummaries.join(', ')}${suffix}`);
    }
  }

  return parts.length > 0 ? parts.join(' ') : 'Matches all events';
}

// ── channel badges ────────────────────────────────────────────────────────────

function describeChannels(template: RuleTemplate): string[] {
  const channels: string[] = [];
  if (template.itsm_config) channels.push('ITSM ticket');
  const targets = templateNotificationsToTargets(template.notifications);
  if (targets.length > 0) {
    const types = [...new Set(targets.map((t) => t.channel))];
    channels.push(`Notification (${types.join(', ')})`);
  }
  if (template.automation_prompt_id) channels.push('Automation');
  return channels;
}

// ── component ─────────────────────────────────────────────────────────────────

interface TemplatePreviewProps {
  templateId: string;
  owner: TemplateOwnerType;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ templateId, owner }) => {
  const { customerId } = useParams<{ customerId: string }>();
  const eventService = useEventManagementService();
  const [template, setTemplate] = useState<RuleTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!templateId || !customerId) return;
    let cancelled = false;
    setLoading(true);
    setTemplate(null);
    eventService
      .getTemplate(templateId, owner, customerId)
      .then((result) => {
        if (!cancelled) setTemplate(result);
      })
      .catch(() => {
        // preview is best-effort — silently ignore fetch failures
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, owner, customerId]);

  if (!customerId) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', py: 0.5, px: 1 }}>
        Preview unavailable
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">Loading template…</Typography>
      </Box>
    );
  }

  if (!template) return null;

  const isScheduled = template.trigger_type === 'schedule';
  const channels = describeChannels(template);

  return (
    <Box
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Signal summary line */}
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        {isScheduled ? 'Scheduled trigger' : 'Watches for'}
      </Typography>

      {isScheduled ? (
        <Typography variant="body2">
          Runs on schedule: <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{template.schedule_expression ?? '—'}</Box>
        </Typography>
      ) : (
        <Typography variant="body2">
          {template.event_pattern
            ? summarizeEventPattern(template.event_pattern)
            : 'No event pattern configured'}
        </Typography>
      )}

      {/* Channels */}
      {channels.length > 0 && (
        <>
          <Divider sx={{ my: 0.75 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
            Drives
          </Typography>
          <Typography variant="body2">{channels.join(' · ')}</Typography>
        </>
      )}

      {/* Raw pattern toggle — escape hatch for technical users */}
      {!isScheduled && template.event_pattern && (
        <>
          <Collapse in={showRaw}>
            <Box
              component="pre"
              sx={{
                mt: 0.75,
                p: 1,
                bgcolor: 'background.paper',
                borderRadius: 0.5,
                overflow: 'auto',
                fontSize: '0.72rem',
                fontFamily: 'monospace',
                maxHeight: 160,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {JSON.stringify(template.event_pattern, null, 2)}
            </Box>
          </Collapse>
          <Box
            component="button"
            onClick={() => setShowRaw((v) => !v)}
            sx={{
              mt: 0.5,
              background: 'none',
              border: 'none',
              p: 0,
              cursor: 'pointer',
              color: 'text.secondary',
              fontSize: '0.72rem',
              '&:hover': { color: 'text.primary' },
            }}
          >
            {showRaw ? 'Hide raw pattern' : 'Show raw pattern'}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TemplatePreview;
